// Line-items readiness reference for the Order Details checklist (ticket 86d385wc0).
//
// The line-items table / extraction / editing workspace are OUT of scope and
// untouched — this derives a READ-ONLY status the readiness bar surfaces as a
// non-interactive reference (option #2). The submit gate mirrors
// SubmitSalesOrderRequestDtoValidator: ≥1 line with MatchedItemId + GradeCode +
// Quantity > 0.

import type { SalesOrderLineItemDto } from '../../types/salesOrder.types';
import { evaluateLinesTonnage } from '../../utils/tonnageGate';

export interface LineItemsReadiness {
  total: number;
  /** Lines that satisfy the submit shape (matched SKU + grade + qty > 0). */
  ready: number;
  /** Lines still missing one of those. */
  needsAttention: number;
  /** Blocker label when the submit gate isn't met; null when satisfied. */
  blocker: string | null;
  /** One-line read-only reference for the readiness bar. */
  summary: string;
}

// A matched line has a usable price for its chosen level (Average -> averagePricePerPc,
// else Base -> basePricePerPc). A blank price level is not usable. Price level drives
// the rate, so it's part of the submit gate — no null computation inputs.
export function hasResolvablePrice(li: SalesOrderLineItemDto): boolean {
  if (!li.priceLevel) return false;
  const base = li.priceLevel === '2' ? li.averagePricePerPc : li.basePricePerPc;
  return base != null;
}

function isSubmitReady(li: SalesOrderLineItemDto): boolean {
  return Boolean(li.matchedItemId && li.gradeCode && (li.quantity ?? 0) > 0) && hasResolvablePrice(li);
}

/**
 * @param foBalanceKg the linked FO's live balance; when given, a total past it by more than the rounding
 *   allowance is a blocker (14ykpqtv3qx) — the server refuses it anyway, after a round trip.
 */
export function deriveLineItemsReadiness(
  items: SalesOrderLineItemDto[],
  foBalanceKg?: number | null,
): LineItemsReadiness {
  const total = items.length;
  const ready = items.filter(isSubmitReady).length;
  const needsAttention = total - ready;

  // Matched + graded + qty lines that lack a usable price level — the specific new gate.
  const missingPrice = items.filter(
    (li) => li.matchedItemId && li.gradeCode && (li.quantity ?? 0) > 0 && !hasResolvablePrice(li),
  ).length;

  const tonnage = foBalanceKg != null && ready > 0 ? evaluateLinesTonnage(items, foBalanceKg) : null;

  const blocker =
    ready === 0
      ? 'At least 1 matched line item (grade + qty + price level)'
      : missingPrice > 0
        ? `${missingPrice} line${missingPrice === 1 ? '' : 's'} need a price level`
        : tonnage?.isRefused
          ? `Total is ${tonnage.overBalanceKg.toLocaleString('en-PH', { maximumFractionDigits: 2 })} kg over the Firm Offer balance`
          : null;

  const summary =
    total === 0
      ? 'No line items yet'
      : needsAttention > 0
        ? `${needsAttention} of ${total} item${total === 1 ? '' : 's'} need attention`
        : `${total} item${total === 1 ? '' : 's'} ready`;

  return { total, ready, needsAttention, blocker, summary };
}
